import React from 'react';
import { useAllLegalDocuments, useUpdateLegalDocument, LegalDocument } from '@/hooks/useLegalDocument';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const LegalDocumentsAdmin: React.FC = () => {
  const { data: docs, isLoading } = useAllLegalDocuments();
  const update = useUpdateLegalDocument();
  const [drafts, setDrafts] = React.useState<Record<string, LegalDocument>>({});

  React.useEffect(() => {
    if (docs) {
      const map: Record<string, LegalDocument> = {};
      docs.forEach((d) => (map[d.id] = d));
      setDrafts(map);
    }
  }, [docs]);

  const handleSave = async (doc: LegalDocument) => {
    try {
      await update.mutateAsync({
        id: doc.id,
        slug: doc.slug,
        title: doc.title,
        content: doc.content,
        published: doc.published,
      });
      toast.success(`${doc.title} saved`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save');
    }
  };

  if (isLoading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Legal Documents</h1>
        <p className="text-gray-600 mt-1">
          Edit the Privacy Policy, Terms of Service, and Copyright Notice shown on the public site.
          Markdown is supported.
        </p>
      </div>

      {Object.values(drafts).map((doc) => (
        <Card key={doc.id}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="capitalize">{doc.slug}</CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor={`pub-${doc.id}`} className="text-sm">Published</Label>
              <Switch
                id={`pub-${doc.id}`}
                checked={doc.published}
                onCheckedChange={(v) => setDrafts((s) => ({ ...s, [doc.id]: { ...doc, published: v } }))}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={doc.title}
                onChange={(e) => setDrafts((s) => ({ ...s, [doc.id]: { ...doc, title: e.target.value } }))}
              />
            </div>
            <div>
              <Label>Content (Markdown)</Label>
              <Textarea
                rows={16}
                value={doc.content}
                onChange={(e) => setDrafts((s) => ({ ...s, [doc.id]: { ...doc, content: e.target.value } }))}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Public URL: /{doc.slug}</span>
              <span>Updated: {new Date(doc.updated_at).toLocaleString()}</span>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => handleSave(doc)} disabled={update.isPending}>
                {update.isPending ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default LegalDocumentsAdmin;
